import React, { FC, useEffect } from "react";
import { Flex } from "@UI/Components/Flex/Flex";
import { Heading } from "@UI/Components/Heading/Heading";
import { usePayFlow } from "@modules/PaymentProcessing/hooks/usePayFlow";
import { PaymentFlowLayout } from "../../../Components/Layouts/PaymentFlowLayout/PaymentFlowLayout";
import { Box } from "@UI/Components/Box/Box";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";
import { PaymentMethodType } from "@modules/PaymentProcessing/types";
import { IOutlets } from "@Libs/Transclusion/types/outlets";
import { processPayment } from "@modules/PaymentProcessing/services/payments";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";
import useI18n from "@I18n/hooks/useI18n";
import { useToast } from "@UI/hooks/useToast";
import { PayWithBancontact } from "@modules/PaymentProcessing/modules/Bancontact/Components/PayWithBancontact/PayWithBancontact";
import { PayWithCreditCard } from "@modules/PaymentProcessing/modules/CreditCard/Components/PayWithCreditCard/PayWithCreditCard";
import { PayByBankTransfer } from "@modules/PaymentProcessing/modules/BankTransfer/Components/PayByBankTransfer/PayByBankTransfer";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";

export const ConfigurePayment: FC = () => {
  const {
    state: {
      resourceId,
      resourceType,
      paymentFormMessage,
      relatedServiceId,
      allowedPaymentMethods,
    },
    onSuccess,
    onPageMount,
    rootRoute,
  } = usePayFlow();

  const isAllowedPaymentMethodSingle =
    Array.isArray(allowedPaymentMethods) && allowedPaymentMethods.length === 1;

  const navigate = useNavigate();
  const paymentMethod = useSearchParams("paymentMethod") as PaymentMethodType;
  const toast = useToast();
  const { formatMessage } = useI18n();

  const handleSubmit: IOutlets["configure-payment.body"]["props"]["onSubmit"] =
    async ({ paymentMethod, provider, isPaymentConfirmed }, callbacks) => {
      try {
        const response = await processPayment({
          paymentProvider: provider,
          paymentMethod,
          resourceId,
          resourceType,
          paymentMethodDetails: {},
          relatedServiceId,
        });
        callbacks?.onSuccess && (await callbacks.onSuccess());
        onSuccess?.({ paymentMethod, provider, isPaymentConfirmed });
        return response;
      } catch (error) {
        // Reload page if payment is already processed to refresh the order state
        if (error?.response?.data?.error === "already_paid") {
          window.location.reload();
          return;
        } else {
          callbacks?.onError && (await callbacks.onError(error));
        }

        throw error;
      }
    };

  const handleBack = () => {
    isAllowedPaymentMethodSingle
      ? navigate("", { back: true })
      : navigate(rootRoute.to, rootRoute);
  };

  const handleSetupError = (error?: Error) => {
    handleBack();

    if (error?.message !== "already_paid") {
      toast({
        title: formatMessage(messages.setupErrorTitle),
        status: "error",
        position: "bottom",
        duration: 9000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    onPageMount?.("CONFIGURE_PAYMENT");
  }, []);

  return (
    <PaymentFlowLayout>
      <PaymentFlowLayout.Nav>
        <BackLink onClick={handleBack} />
      </PaymentFlowLayout.Nav>
      <PaymentFlowLayout.Header>
        <Flex alignItems="center" justifyContent={["space-between", "center"]}>
          <Heading as="h3" pr={{ base: 6, md: 0 }} flexShrink={1}>
            <FormattedMessage {...messages.completeYourInformation} />
          </Heading>
        </Flex>
      </PaymentFlowLayout.Header>
      <PaymentFlowLayout.Content>
        <Box
          w="full"
          alignItems="center"
          justifyContent="center"
          data-testid="configure-payment"
        >
          <Box
            shadow="lg"
            bg="white"
            p={[5, 5, 10]}
            borderRadius="xl"
            alignContent="center"
            data-testid="configure-payment-method"
          >
            {paymentMethod === PaymentMethodType.CREDIT_CARD && (
              <PayWithCreditCard
                onBack={handleBack}
                onSubmit={handleSubmit}
                onSetupError={handleSetupError}
                isAllowedPaymentMethodSingle={isAllowedPaymentMethodSingle}
              />
            )}

            {paymentMethod === PaymentMethodType.BANCONTACT && (
              <PayWithBancontact
                onBack={handleBack}
                onSubmit={handleSubmit}
                onSetupError={handleSetupError}
                isAllowedPaymentMethodSingle={isAllowedPaymentMethodSingle}
              />
            )}

            {paymentMethod === PaymentMethodType.BANK_TRANSFER && (
              <PayByBankTransfer
                onBack={handleBack}
                onSubmit={handleSubmit}
                onSetupError={handleSetupError}
                isAllowedPaymentMethodSingle={isAllowedPaymentMethodSingle}
              />
            )}
          </Box>
          <Box mt="5">{paymentFormMessage}</Box>
        </Box>
      </PaymentFlowLayout.Content>
    </PaymentFlowLayout>
  );
};
